/* global Sanki */
/**
 * Sanki — api.js
 * Typed thin wrapper around Shopify's AJAX cart + predictive search endpoints.
 * All endpoints return parsed JSON and emit `sanki:cart:change` on mutation.
 */
(function (global) {
  'use strict';

  const Sanki = (global.Sanki = global.Sanki || {});
  const routes = Sanki.routes || {};
  const api = (Sanki.api = Sanki.api || {});

  function request(url, options = {}) {
    const opts = Object.assign({
      method: 'GET',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }, options);
    if (opts.body && !(opts.body instanceof FormData) && typeof opts.body === 'object') {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(opts.body);
    }
    return fetch(url, opts).then(async (res) => {
      const text = await res.text();
      let data;
      try { data = text ? JSON.parse(text) : {}; } catch (_) { data = { raw: text }; }
      if (!res.ok) {
        const err = new Error(data.description || data.message || `Request failed (${res.status})`);
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data;
    });
  }

  /* ---- Cart ------------------------------------------------------------- */

  api.cart = {
    fetch() {
      return request((routes.cart || '/cart') + '.js');
    },

    /**
     * Add one or more items. Accepts { id, quantity } or { items: [...] }.
     */
    add(input) {
      const body = Array.isArray(input)
        ? { items: input }
        : (input && input.items) ? input
          : { items: [input] };
      return request(routes.cartAdd || '/cart/add.js', { method: 'POST', body })
        .then(res => api.cart.fetch().then(cart => (emitChange(cart), { added: res, cart })));
    },

    change(payload) {
      return request(routes.cartChange || '/cart/change.js', { method: 'POST', body: payload })
        .then(cart => (emitChange(cart), cart));
    },

    update(payload) {
      return request(routes.cartUpdate || '/cart/update.js', { method: 'POST', body: payload })
        .then(cart => (emitChange(cart), cart));
    },

    clear() {
      return request('/cart/clear.js', { method: 'POST' })
        .then(cart => (emitChange(cart), cart));
    }
  };

  function emitChange(cart) {
    document.dispatchEvent(new CustomEvent('sanki:cart:change', { detail: { cart } }));
  }

  /* ---- Predictive search ------------------------------------------------ */

  api.search = {
    /**
     * @param {string} q
     * @param {object} [opts]  e.g. { resources: { type: ['product'], limit: 6 } }
     */
    predict(q, opts) {
      const url = new URL((routes.predictiveSearch || '/search/suggest') + '.json', location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('resources[type]', (opts && opts.type) || 'product');
      url.searchParams.set('resources[limit]', (opts && opts.limit) || 6);
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      return request(url.toString());
    }
  };

  /* ---- Section rendering ------------------------------------------------ */

  api.sections = {
    /**
     * Fetch a rendered section HTML fragment by section id.
     */
    fetch(sectionId, params) {
      const url = new URL(location.pathname, location.origin);
      url.searchParams.set('section_id', sectionId);
      if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
      return fetch(url.toString()).then(r => r.text());
    }
  };

  /* ---- Wishlist (client-side; optional /apps/sanki/wishlist backend) --- */

  const WL_KEY = 'sanki:wishlist';

  api.wishlist = {
    list() {
      return Sanki.helpers.storage.get(WL_KEY, []);
    },
    has(id) {
      return api.wishlist.list().some(x => String(x.id) === String(id));
    },
    add(item) {
      const list = api.wishlist.list();
      if (!api.wishlist.has(item.id)) {
        // Stamp the add-time so the wishlist page can sort by recency.
        list.push(Object.assign({ addedAt: Date.now() }, item));
      }
      Sanki.helpers.storage.set(WL_KEY, list);
      document.dispatchEvent(new CustomEvent('sanki:wishlist:change', { detail: { list } }));
      // Best-effort backend sync (no-op if the app proxy isn't wired up).
      if (window.Sanki.customerId) {
        fetch('/apps/sanki/wishlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'add', item })
        }).catch(() => {});
      }
      return list;
    },
    remove(id) {
      const list = api.wishlist.list().filter(x => String(x.id) !== String(id));
      Sanki.helpers.storage.set(WL_KEY, list);
      document.dispatchEvent(new CustomEvent('sanki:wishlist:change', { detail: { list } }));
      if (window.Sanki.customerId) {
        fetch('/apps/sanki/wishlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'remove', id })
        }).catch(() => {});
      }
      return list;
    },
    toggle(item) {
      return api.wishlist.has(item.id) ? api.wishlist.remove(item.id) : api.wishlist.add(item);
    },
    /**
     * Wipe the wishlist in one atomic write. Iterating with .remove() on
     * each item works in theory, but mixing JSON.parse/stringify per call
     * with re-render side-effects has caused inconsistent state in a few
     * browsers — clear() is one read, one write, one event dispatch.
     */
    clear() {
      Sanki.helpers.storage.set(WL_KEY, []);
      document.dispatchEvent(new CustomEvent('sanki:wishlist:change', { detail: { list: [] } }));
      if (window.Sanki.customerId) {
        fetch('/apps/sanki/wishlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'clear' })
        }).catch(() => {});
      }
      return [];
    }
  };
})(typeof window !== 'undefined' ? window : this);
